import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ActivityDTO,
  BalanceDTO,
  CampaignDTO,
  CircleDTO,
  CircleDetailDTO,
  Currency,
  Frequency,
  NotificationDTO,
  PaymentRequestDTO,
  UserDTO,
  PlanOverviewDTO,
  CardDTO,
} from "@cafonapay/shared";
import { formatMoney } from "@cafonapay/shared/client";
import { useCallback } from "react";
import { api, RequestError } from "./api";
import { intlLocale, useI18n } from "./i18n";

export const keys = {
  me: ["me"] as const,
  wallet: ["wallet"] as const,
  activity: ["activity"] as const,
  circles: ["circles"] as const,
  circle: (id: string) => ["circle", id] as const,
  invite: (code: string) => ["invite", code] as const,
  campaigns: ["campaigns"] as const,
  campaign: (slug: string) => ["campaign", slug] as const,
  requests: ["requests"] as const,
  request: (id: string) => ["request", id] as const,
};

export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: async () => {
      try {
        return (await api.get<{ user: UserDTO }>("/auth/me")).user;
      } catch (e) {
        if (e instanceof RequestError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 5 * 60_000,
  });
}

export function useWallet(enabled = true) {
  return useQuery({ queryKey: keys.wallet, queryFn: () => api.get<{ balances: BalanceDTO[]; mode: string }>("/wallet"), enabled });
}

export function useActivity(limit = 30) {
  return useQuery({
    queryKey: [...keys.activity, limit],
    queryFn: () => api.get<{ items: ActivityDTO[]; nextBefore: string | null }>(`/wallet/activity?limit=${limit}`),
  });
}

export type CircleSummary = CircleDTO & { iPaidCurrentCycle: boolean };

export function useCircles() {
  return useQuery({ queryKey: keys.circles, queryFn: async () => (await api.get<{ items: CircleSummary[] }>("/circles")).items });
}

export function useCircle(id: string) {
  return useQuery({ queryKey: keys.circle(id), queryFn: async () => (await api.get<{ circle: CircleDetailDTO }>(`/circles/${id}`)).circle });
}

export interface InviteDTO {
  circleId: string;
  name: string;
  description: string | null;
  contributionMinor: number;
  currency: Currency;
  frequency: Frequency;
  memberCount: number;
  joined: number;
  startDate: string;
  status: CircleDTO["status"];
  organizer: { name: string; handle: string } | null;
  alreadyMember: boolean;
  mode?: "wallet" | "coordination";
}

export function useInvite(code: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.invite(code),
    queryFn: async () => (await api.get<{ invite: InviteDTO }>(`/circles/invite/${code}`)).invite,
    enabled,
    retry: false,
  });
}

export function useCampaigns() {
  return useQuery({ queryKey: keys.campaigns, queryFn: async () => (await api.get<{ items: CampaignDTO[] }>("/campaigns")).items });
}

export function useCampaign(slug: string) {
  return useQuery({
    queryKey: keys.campaign(slug),
    queryFn: async () => (await api.get<{ campaign: CampaignDTO }>(`/campaigns/${slug}`)).campaign,
    retry: false,
  });
}

export function useRequests() {
  return useQuery({ queryKey: keys.requests, queryFn: () => api.get<{ sent: PaymentRequestDTO[]; received: PaymentRequestDTO[] }>("/requests") });
}

export function usePaymentRequest(id: string) {
  return useQuery({
    queryKey: keys.request(id),
    queryFn: async () => (await api.get<{ request: PaymentRequestDTO }>(`/requests/${id}`)).request,
    retry: false,
  });
}

/** After any money movement, refresh everything that shows balances. */
export function useInvalidateMoney() {
  const qc = useQueryClient();
  return useCallback(() => {
    for (const k of [keys.wallet, keys.activity, keys.circles, keys.campaigns, keys.requests, ["plan"], ["cards"]]) qc.invalidateQueries({ queryKey: k });
  }, [qc]);
}

/** Public feature flags: which features are on and whether they run on real money. */
export function useFeatures() {
  return useQuery({
    queryKey: ["features"],
    queryFn: () => api.get<{ mode: string; flags: Array<{ id: string; enabled: boolean; realMoney: boolean }> }>("/features"),
    staleTime: 10 * 60_000,
  });
}

export function useNotifications() {
  return useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get<{ items: NotificationDTO[]; unread: number }>("/notifications"),
    refetchInterval: 60_000,
  });
}

export function useSignOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/auth/logout"),
    onSettled: () => {
      qc.clear();
      qc.setQueryData(keys.me, null);
    },
  });
}

export function useMoney() {
  const { locale } = useI18n();
  return useCallback((minor: number, currency: Currency) => formatMoney(minor, currency, intlLocale(locale)), [locale]);
}

export function useDate() {
  const { locale } = useI18n();
  return useCallback(
    (iso: string, opts: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" }) => {
      const date = iso.length === 10 ? new Date(`${iso}T12:00:00Z`) : new Date(iso);
      return new Intl.DateTimeFormat(intlLocale(locale), { timeZone: iso.length === 10 ? "UTC" : undefined, ...opts }).format(date);
    },
    [locale],
  );
}

/** The member's local date (the plan's "today" follows their clock, not UTC). */
export function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function usePlan() {
  return useQuery({ queryKey: ["plan"], queryFn: () => api.get<PlanOverviewDTO>(`/plan?today=${localToday()}`) });
}

export function useCards() {
  return useQuery({ queryKey: ["cards"], queryFn: () => api.get<{ items: CardDTO[]; mode: string }>("/card") });
}
